import { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";

import { FaTimes, FaStickyNote, FaPlus, FaTrash } from "react-icons/fa";

import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import useCommon from "hooks/useCommon";
import { getReservationNotes } from "store/slice/guestSlice";
import { resetCreateNoteState } from "store/slice/guestSlice";
import { createReservationNote } from "store/slice/guestSlice";
import dayjs from "dayjs";
import { CircularProgress } from "@mui/material";
import { deleteReservationNote } from "store/slice/guestSlice";
import usePermissions from "hooks/usePermissions";

const AddStaffNoteModal = ({ open, guest, onClose, onSave, canEdit = false }) => {
  const { STORE, DISPATCH } = useCommon();
  const { can } = usePermissions();

  const { notes, notesLoading, createNoteLoading, deleteNoteLoading } = STORE(
    (state) => state.guests
  );

  const [note, setNote] = useState("");
  const [deletingNoteId, setDeletingNoteId] = useState(null);

  const { black, dark, primary, success, light } = colors;
  const { borderWidth } = borders;

  useEffect(() => {
    if (!open) setNote("");
  }, [open]);

  const handleSave = async () => {
    if (!note.trim() || !guest) return;

    await DISPATCH(
      createReservationNote({
        hotelId: guest.hotelId,
        guestId: guest.id,
        reservationId: guest.reservationId,
        data: {
          text: note,
          category: "staff_note",
          visibility: "staff",
        },
      })
    );

    setNote("");
  };
  const handleDelete = async (noteId) => {
    if (!guest) return;

    setDeletingNoteId(noteId);

    await DISPATCH(
      deleteReservationNote({
        hotelId: guest.hotelId,
        guestId: guest.id,
        reservationId: guest.reservationId,
        noteId,
      })
    );

    setDeletingNoteId(null);
  };

  useEffect(() => {
    if (!open || !guest) return;

    DISPATCH(
      getReservationNotes({
        hotelId: guest.hotelId,
        guestId: guest.id,
        reservationId: guest.reservationId,
        query: {
          category: "staff_note",
          visibility: "staff",
        },
      })
    );
  }, [open, guest, DISPATCH]);

  useEffect(() => {
    if (!open) {
      setNote("");
      DISPATCH(resetCreateNoteState());
    }
  }, [open, DISPATCH]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {/* ================= HEADER ================= */}
      <DialogTitle sx={{ pb: 1 }}>
        <VuiBox display="flex" alignItems="center" justifyContent="space-between">
          <VuiBox display="flex" alignItems="center" gap={1}>
            <FaStickyNote size={16} color={primary.main} />
            <VuiTypography variant="lg">Staff Notes</VuiTypography>
          </VuiBox>

          <IconButton size="small" onClick={onClose}>
            <FaTimes color={primary.main} />
          </IconButton>
        </VuiBox>

        {guest && (
          <VuiTypography variant="caption" mt={0.5}>
            {guest.name}
            {guest.room ? ` · ${guest.room}` : ""}
          </VuiTypography>
        )}
      </DialogTitle>

      {/* ================= CONTENT ================= */}
      <DialogContent
        sx={{
          mt: 0,
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
          overflow: "hidden",
        }}
      >
        {/* Notes list */}
        <VuiBox
          flex={1}
          overflow="auto"
          pr={1}
          display="flex"
          flexDirection="column"
          gap={2}
          mb={2}
        >
          {notesLoading ? (
            <VuiBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              py={4}
              gap={1}
            >
              <CircularProgress size={20} />

              <VuiTypography variant="caption">Loading notes...</VuiTypography>
            </VuiBox>
          ) : notes.length === 0 ? (
            <VuiTypography variant="caption" textAlign="center">
              No staff notes added yet.
            </VuiTypography>
          ) : (
            notes?.map((n) => (
              <VuiBox
                key={n.id}
                p={1.75}
                // mx={1}
                position="relative"
                borderRadius="14px"
                sx={{
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
                  border: "1px solid rgba(255,255,255,0.12)",
                }}
              >
                <VuiBox display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Avatar
                    sx={{
                      width: 22,
                      height: 22,
                      fontSize: 11,
                      color: primary.main,
                      background: rgba(primary.main, 0.1),
                      border: `${borderWidth[1]} solid ${rgba(primary.main, 0.18)}`,
                    }}
                  >
                    {n.authorName?.[0] || "S"}
                  </Avatar>

                  <VuiTypography variant="caption">{n.authorName || "Not defined"} ·</VuiTypography>

                  <VuiTypography variant="caption">
                    {dayjs(n.createdAt).format("MMM D · h:mm A")}
                  </VuiTypography>
                  {canEdit && can("deleteInHouseGuests") && (
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(n._id)}
                      disabled={deletingNoteId === n._id}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        "&:hover svg": {
                          color: "#ff4d4f",
                        },
                      }}
                    >
                      {deletingNoteId === n._id ? (
                        <CircularProgress size={14} />
                      ) : (
                        <FaTrash size={10} color="#ff4d4f" />
                      )}
                    </IconButton>
                  )}
                </VuiBox>

                <VuiTypography
                  mt={1.5}
                  variant="body2"
                  lineHeight={1.4}
                  sx={{
                    fontSize: "14px",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                    whiteSpace: "pre-line",
                  }}
                >
                  {n.text}
                </VuiTypography>
              </VuiBox>
            ))
          )}
        </VuiBox>

        {/* ================= ADD NOTE (EDIT MODE ONLY) ================= */}
        {canEdit && can("editInHouseGuests") && (
          <VuiBox>
            <VuiTypography variant="caption" mb={0.5}>
              Add new note
            </VuiTypography>

            <VuiInput
              gradient
              multiline
              rows={3}
              placeholder="Write a new staff note…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              inputProps={{ maxLength: 250 }}
            />

            <VuiBox display="flex" justifyContent="flex-end" mt={2}>
              <VuiButton
                color="primary"
                size="small"
                startIcon={<FaPlus />}
                disabled={!note.trim() || createNoteLoading || deleteNoteLoading || notesLoading}
                onClick={handleSave}
              >
                {createNoteLoading ? "Adding..." : "Add Note"}
              </VuiButton>
            </VuiBox>
          </VuiBox>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddStaffNoteModal;
